import moment from 'moment';

// "Link your events" (Set apply, and single-code applies since CON-2033:
// pass singleCode = true for a keyword, Smart Link or Smart Form code). Pure helpers shared by the step, the
// picker and the test: which cards show, how each card's Event Type reads,
// whether a card is complete, the eventChoices body, and the outcome lines.

export const ACTION_EXISTING = 'existing';
export const ACTION_CREATE = 'create';
export const ACTION_SKIP = 'skip';

export const USED_BY_TYPE_LABELS = {
  keyword: 'Keyword',
  smartLink: 'Smart Link',
  formRule: 'Smart Form rule',
};

const SELECTION_BY_ITEM_KEYS = 'itemKeys';

/** *************************************************************************
 * selectionByItemKeys
 * CON-2033 adapter for the Set update picker. Its cards carry itemKeys
 * ("keyword:123") instead of setItemIds, and a copied Type carries
 * eventType.itemKey ("partyType:<id>") instead of setItemId. Pass the result
 * anywhere these helpers (and LinkYourEventsStep) take selectedIds; a plain
 * array keeps meaning "checked Set item ids".
 *************************************************************************** */
export function selectionByItemKeys(itemKeys = []) {
  return { kind: SELECTION_BY_ITEM_KEYS, keys: [...(itemKeys || [])].map(String) };
}

// What is selected, which keys a card came in through, and the key of the
// item that copies its Event Type, for either kind of selection.
const toSelection = (selectedIds) => {
  if (selectedIds && selectedIds.kind === SELECTION_BY_ITEM_KEYS) {
    const keys = new Set(selectedIds.keys || []);
    return {
      has: (key) => key != null && keys.has(String(key)),
      cardKeys: (card) => card?.itemKeys || [],
      typeKey: (eventType) => eventType?.itemKey,
    };
  }
  const ids = new Set((Array.isArray(selectedIds) ? selectedIds : []).map(Number));
  return {
    has: (id) => id != null && ids.has(Number(id)),
    cardKeys: (card) => card?.setItemIds || [],
    typeKey: (eventType) => eventType?.setItemId,
  };
};

/** *************************************************************************
 * getVisibleEventLinks
 * A card shows only while at least one of the Set items it came in through
 * is checked (or, with selectionByItemKeys, one of its update items).
 *************************************************************************** */
export function getVisibleEventLinks(eventLinks = [], selectedIds = []) {
  const selection = toSelection(selectedIds);
  return (eventLinks || []).filter((card) => selection.cardKeys(card)
    .some((key) => selection.has(key)));
}

/** *************************************************************************
 * resolveEventType
 * How "Create a new event" shows the Event Type for this card:
 *   { readOnly: true, label }  the Set copies it now, or the user already has
 *                              a copy. No partyTypeId is sent.
 *   { readOnly: false }        the user picks one of their own Types.
 * singleCode (CON-2033): always the picker.
 *************************************************************************** */
export function resolveEventType(card = {}, selectedIds = [], singleCode = false) {
  // CON-2033: a single code (keyword, Smart Link, Smart Form) never copies an
  // Event Type, so the user always picks one of theirs.
  if (singleCode) return { readOnly: false };
  const eventType = card?.eventType || {};
  const selection = toSelection(selectedIds);
  if (eventType.mode === 'copied' && selection.has(selection.typeKey(eventType))) {
    return { readOnly: true, label: `${eventType.name || 'Event Type'} (added with this Set)` };
  }
  if (eventType.existingPartyTypeId) {
    return { readOnly: true, label: eventType.existingName || eventType.name || 'Event Type' };
  }
  return { readOnly: false };
}

/** *************************************************************************
 * findSameNameType
 * The user's Event Type whose name matches the author's (case-insensitive),
 * used to preselect the picker. Null when there is none.
 *************************************************************************** */
export function findSameNameType(partyTypes = [], name = '') {
  const target = String(name || '').trim().toLowerCase();
  if (!target) return null;
  return (partyTypes || []).find((type) => String(type?.name || '').trim().toLowerCase() === target) || null;
}

/** *************************************************************************
 * findPreselectType
 * The Event Type the picker starts on. A single code (CON-2033) prefers the
 * user's own copy of the author's Type (existingPartyTypeId) when it is in
 * the list, then the same-name match. A Set uses the same-name match only.
 *************************************************************************** */
export function findPreselectType(partyTypes = [], card = {}, singleCode = false) {
  const eventType = card?.eventType || {};
  if (singleCode && eventType.existingPartyTypeId) {
    const own = (partyTypes || []).find((type) => String(type?.id) === String(eventType.existingPartyTypeId));
    if (own) return own;
  }
  return findSameNameType(partyTypes, eventType.name);
}

/** *************************************************************************
 * sortEventsForPicker
 * Upcoming first (start today or later, soonest first), then past events,
 * newest first.
 *************************************************************************** */
export function sortEventsForPicker(parties = [], now = new Date()) {
  const startOfToday = moment(now).startOf('day').valueOf();
  const startOf = (party) => {
    const value = moment(party?.partyStartDateTime);
    return value.isValid() ? value.valueOf() : null;
  };
  const upcoming = [];
  const past = [];
  (parties || []).forEach((party) => {
    const start = startOf(party);
    if (start != null && start >= startOfToday) upcoming.push(party);
    else past.push(party);
  });
  upcoming.sort((a, b) => startOf(a) - startOf(b));
  past.sort((a, b) => (startOf(b) ?? -Infinity) - (startOf(a) ?? -Infinity));
  return [...upcoming, ...past];
}

// The host value is an Autocomplete option { value, label } or a bare id.
const hostIdOf = (host) => {
  const id = host && typeof host === 'object' ? host.value : host;
  const num = Number(id);
  return Number.isInteger(num) && num > 0 ? num : null;
};

const typeIdOf = (value) => {
  const num = Number(value);
  return Number.isInteger(num) && num > 0 ? num : null;
};

/** *************************************************************************
 * isEventNameTaken
 * True when the name matches one of the user's own event names, trimmed
 * and ignoring case (the server's create check is the backstop).
 *************************************************************************** */
export function isEventNameTaken(name = '', eventNames = []) {
  const target = String(name || '').trim().toLowerCase();
  if (!target) return false;
  return (eventNames || []).some((other) => String(other || '').trim().toLowerCase() === target);
}

const isValidDate = (value) => value != null && value !== '' && moment(value).isValid();

/** *************************************************************************
 * isChoiceComplete
 * A card is complete when it has a choice, and:
 *   existing  an event is picked
 *   create    name (not one of the user's own, create.nameTaken), host,
 *             starts and ends (ends not before starts), and an Event Type
 *             when the Type is a picker
 *   skip      nothing more
 *************************************************************************** */
export function isChoiceComplete(card = {}, choice = null, selectedIds = [], singleCode = false) {
  if (!choice?.action) return false;
  if (choice.action === ACTION_SKIP) return true;
  if (choice.action === ACTION_EXISTING) return typeIdOf(choice.partyId) != null;
  if (choice.action !== ACTION_CREATE) return false;
  const create = choice.create || {};
  if (!String(create.partyName || '').trim()) return false;
  if (create.nameTaken) return false;
  if (hostIdOf(create.host) == null) return false;
  if (!isValidDate(create.partyStartDateTime) || !isValidDate(create.partyEndDateTime)) return false;
  if (moment(create.partyEndDateTime).isBefore(moment(create.partyStartDateTime))) return false;
  if (!resolveEventType(card, selectedIds, singleCode).readOnly
    && typeIdOf(create.partyTypeId) == null) return false;
  return true;
}

export function areAllChoicesComplete(visibleCards = [], choices = {}, selectedIds = [], singleCode = false) {
  return (visibleCards || []).every((card) => isChoiceComplete(
    card,
    choices?.[card.sourcePartyId],
    selectedIds,
    singleCode,
  ));
}

const optionalUrl = (value) => {
  const text = String(value || '').trim();
  return text || null;
};

/** *************************************************************************
 * buildEventChoices
 * The apply body's eventChoices: one entry per visible card. partyTypeId is
 * sent only when the Type was a picker.
 *************************************************************************** */
export function buildEventChoices(visibleCards = [], choices = {}, selectedIds = [], singleCode = false) {
  return (visibleCards || []).map((card) => {
    const choice = choices?.[card.sourcePartyId] || {};
    const { sourcePartyId } = card;
    if (choice.action === ACTION_EXISTING) {
      return { sourcePartyId, action: ACTION_EXISTING, partyId: typeIdOf(choice.partyId) };
    }
    if (choice.action === ACTION_CREATE) {
      const create = choice.create || {};
      const body = {
        partyName: String(create.partyName || '').trim(),
        hostContactId: hostIdOf(create.host),
        // Sent as Add Event sends it; the host is still the contact above
        // (your own contact when Hosted by You? is checked).
        hostedByConsultant: create.hostedByConsultant === true,
        partyStartDateTime: isValidDate(create.partyStartDateTime)
          ? moment(create.partyStartDateTime).toISOString() : null,
        partyEndDateTime: isValidDate(create.partyEndDateTime)
          ? moment(create.partyEndDateTime).toISOString() : null,
        partyURL: optionalUrl(create.partyURL),
        partyShoppingURL: optionalUrl(create.partyShoppingURL),
        hostURL: optionalUrl(create.hostURL),
      };
      if (!resolveEventType(card, selectedIds, singleCode).readOnly) {
        body.partyTypeId = typeIdOf(create.partyTypeId);
      }
      return { sourcePartyId, action: ACTION_CREATE, create: body };
    }
    return { sourcePartyId, action: ACTION_SKIP };
  });
}

/** *************************************************************************
 * eventNamesBySource
 * The name the user saw for each choice (the event they picked, or the one
 * they asked us to create), keyed by sourcePartyId. The apply response does
 * not carry the recipient's event names.
 *************************************************************************** */
export function eventNamesBySource(choices = {}) {
  const names = {};
  Object.entries(choices || {}).forEach(([sourcePartyId, choice]) => {
    if (choice?.action === ACTION_EXISTING && choice.partyName) names[sourcePartyId] = choice.partyName;
    if (choice?.action === ACTION_CREATE && choice.create?.partyName) {
      names[sourcePartyId] = String(choice.create.partyName).trim();
    }
  });
  return names;
}

/** *************************************************************************
 * describeEventLinkOutcome
 * One results line per event in the apply response's eventLinks.events.
 * A failed create is not a failed apply; its line carries the server's
 * reason (for example "Event Name already exists") when there is one.
 *************************************************************************** */
export function describeEventLinkOutcome(event = {}, namesBySource = {}) {
  const name = namesBySource?.[event.sourcePartyId] || event.sourcePartyName || 'your event';
  switch (event.status) {
    case 'linked':
      return `Linked to ${namesBySource?.[event.sourcePartyId] || 'your event'}`;
    case 'created':
      return `Created ${name}`;
    case 'createFailed': {
      const reason = `${event.error || ''}`.trim().replace(/[.!?]+$/, '');
      return reason
        ? `We couldn't create ${name}: ${reason}. These items need an event.`
        : `We couldn't create ${name}. These items need an event.`;
    }
    case 'skipped':
    default:
      return 'Skipped: these items need an event';
  }
}

/** *************************************************************************
 * eventLinkOutcomeTitle
 * The heading over an event's results line: the sender's event name.
 *************************************************************************** */
export function eventLinkOutcomeTitle(event = {}) {
  return `${event.sourcePartyName || ''}`.trim() || 'Their event';
}

/** *************************************************************************
 * describeEventLinkOutcomeText
 * "[their event]: [outcome]" for the one-line notifications (the Smart Link
 * and Smart Form windows close on success).
 *************************************************************************** */
export function describeEventLinkOutcomeText(event = {}, namesBySource = {}) {
  return `${eventLinkOutcomeTitle(event)}: ${describeEventLinkOutcome(event, namesBySource)}`;
}
